import React, { useRef, useState, useCallback, useEffect } from 'react'

import {
    Box,
    Button,
    Tooltip,
    Chip,
    Link
} from '@mui/material';

import { useNavigate } from 'react-router-dom';

import DateFilter from '@inovua/reactdatagrid-community/DateFilter'
import { icons } from '../../common/CommonIcons';
import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonRestricted from '../../common/CommonRestricted';
import DataGridToolbar from '../../common/DataGridToolbar';
import DataGridTable from '../../common/DataGridTable';
import DataGridRowActionMenu from '../../common/DataGridRowActionMenu';
import { useAppContextFeatures } from '../../../context/AppContext';

import { useDataGridParams } from '../../../hooks/useDataGridParams';
import { useDocuments, documentTypes, targetTypes } from '../../../hooks/api/useDocuments';
import { useSystemPrompt } from '../../../hooks/useSystemPrompt';
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useTranslations } from '../../../hooks/useTranslations';
import { useDependencies, dependencyModules, targetTypes as dependencyTargetTypes } from '../../../hooks/api/useDependencies';

export default function ReplacementDeliveriesPage({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@document"] })

    const navigate = useNavigate();
    const { promptMessage } = useSystemPrompt();
    const { list, remove } = useDocuments({ documentType: documentTypes.replacementDelivery });
    const { RenderDatagridDisplay: RenderDependencyDatagridDisplay } = useDependencies()

    const { defineColumns, columns, filterValues, getRenderColumnLink, getRenderColumnTextDisplay } = useDataGridParams('id')
    const [selectedIds, setSelectedIds] = useState([])
    const [refreshDataGrid, setRefreshDataGrid] = useState(0)
    const { getLocationIds } = useAppContextFeatures();

    const gridRef = React.createRef();
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const { isPermissionAllow } = usePermissions(routeType, scopeIds.document.id, scopeTypes.document.replacementDelivery.id)

    const resetDataGridTableSelection = () => {
        setSelectedIds([])
    }

    useEffect(() => {

        setRefreshDataGrid(val => val + 1)

    }, [getLocationIds])

    const fetchData = async (props) => {

        resetDataGridTableSelection();

        try {
            props.dataTypes = ["dependency"]

            props.customFilterLocationEnable = true;
            props.customFilterLocationFields = ['location_id']

            const result = await list(props)

            let rawData = result?.data?.documents

            let newData = []
            
            rawData.forEach(i => {

                let _i = i

                let outward = _i?.outward_dependencies
                let inward = _i?.inward_dependencies

                let all = [...outward, ...inward]

                // declare a value and allocate the combined data
                _i.all_dependencies = all

                newData.push(_i)
            })

            return {
                data: newData,
                count: result?.data?.total
            }

        } catch (error) {

            promptMessage(false, `Error getting results (${error?.message})`)

            return {
                data: [],
                count: 0
            }
        }

    }

    useEffect(() => {

        defineColumnsView()

    }, [])

    const defineColumnsView = () => {

        defineColumns([
            {
                name: 'location_name', header: t('location')
            },
            {
                name: 'doc_number',
                header: t('docNo'),
                renderValue: ({ value, cellProps }) => {

                    if (isPermissionAllow({ functionType: functionTypes.pageView.id }) === false) {
                        return getRenderColumnTextDisplay(value)
                    }

                    let path = getRoute(`documents/replacementDelivery/view/${cellProps.data.id}`);

                    return getRenderColumnLink(path, value)

                }
            },
            {
                name: 'doc_date', header: t('date')
            },
            {
                name: 'user_type',
                header: t('type'),
                renderValue: ({ value, cellProps }) => {

                    return (
                        <Chip
                            label={value}
                            color="primary"
                            variant="outlined"
                            size='small'
                            sx={{
                                // ml: 1,
                                textTransform: 'capitalize'
                            }}
                        />
                    )

                },
            },
            {
                name: 'replacement_delivery_profile_name', header: t('profile')
            },
            {
                name: 'doc_remark', header: t('remark')
            },
            {
                // Merge VerifyQTy and Verify Out into new column named Validation
                name: 'verify_out_status',
                header: t('validation'),
                renderValue: ({ value, cellProps }) => {

                    const doneValidation = cellProps.data.items.reduce((total, item) => total + parseFloat(item?.verify_out_qty), 0)
                    const totalValidation = cellProps.data.items.reduce((total, item) => total + parseFloat(item?.qty), 0)

                    return (
                        <Box
                            display={"flex"}
                            flexDirection={"row"}
                            justifyContent={"flex-start"}
                            alignItems={"center"}
                        >
                            <Tooltip
                                title={
                                    doneValidation === totalValidation ? t("validated") : t("pending")
                                }
                                placement="right"
                            >
                                <Button
                                    size='small'
                                    variant={doneValidation === totalValidation ? "contained" : "outlined"}
                                    color={`primary`}
                                    sx={{
                                        textAlign: "center",
                                        width: 100
                                    }}
                                    onClick={() => {

                                        if (isPermissionAllow({ functionType: functionTypes.pageVerifyOut.id })) {
                                            navigate(getRoute(`documents/replacementDelivery/verifyOut/${cellProps.data?.id}`))
                                        }

                                    }}
                                >
                                    {doneValidation} / {totalValidation}

                                </Button>

                            </Tooltip>

                        </Box>
                    )
                }
            },
            {
                name: 'all_dependencies', 
                header: t('relatedDocs'),
                renderValue: ({ value, cellProps }) => {

                    return (
                        <RenderDependencyDatagridDisplay
                            id={cellProps.data.id}
                            values={value}
                            renderValue={({ id, otherUrl }) => {

                                if(otherUrl){
                                    return getRoute(otherUrl)
                                }
    
                                return getRoute(`documents/replacementDelivery/view/${id}`)
                            }}
                        />
                    )

                }

            },
        ])
    }

    const handleDelete = async () => {

        try {

            let result = await remove({ ids: selectedIds, targetType: targetTypes.replacementDelivery })

            promptMessage(true, result?.message)
            setRefreshDataGrid(val => val + 1)

        } catch (error) {
            promptMessage(false, error?.message)
        }

    }

    const RenderToolbar = () => {

        const getToolbarMainMenus = () => {

            let props = [];

            if (isPermissionAllow({ functionType: functionTypes.pageNew.id })) {
                props.push({
                    // displayText: "New Delivery Orders",
                    displayIconName: icons.plus,
                    onClick: () => {

                        navigate(getRoute("documents/replacementDelivery/new"));

                    }
                })
            }

            return props
        }

        const getActionOnClick = () => {

            let handleFunction = null

            if (isPermissionAllow({ functionType: functionTypes.remove.id })) {
                handleFunction = handleDelete
            }

            return handleFunction
        }

        return (
            <DataGridToolbar
                selectedRowCount={
                    selectedIds.length
                }
                mainMenus={
                    getToolbarMainMenus()
                }
                actionOnClickRemove={
                    getActionOnClick()
                }
                actionMenus={[]}
            />
        )
    }

    const RenderDataGridTable = () => {

        return (
            <DataGridTable
                ref={gridRef}
                refresh={refreshDataGrid}
                fetchData={fetchData}
                dataGrid={{
                    idProperty: "id",
                    style: {
                        minHeight: 600
                    },
                    pageSizes: [20, 50, 100],
                    defaultLimit: 10,
                    defaultSkip: 0,
                    checkboxColumn:
                        isPermissionAllow({ functionType: functionTypes.remove.id }),
                    columns,
                    defaultFilterValue: filterValues,
                    onSelectionChange: (items) => {
                        // console.log('items selected : ', items)
                        setSelectedIds(
                            items
                        )
                    }
                }}

            />
        )

    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.replacementDelivery })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t("replacementDeliveries")}
                breadcrumbs={[
                    {
                        displayText: t("replacementDeliveries"),
                        props: {
                            // underline: "hover",
                            // href: "/"
                        }
                    }
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageList.id }) ?
                    <>
                        {
                            RenderToolbar()
                        }
                        {
                            RenderDataGridTable()
                        }
                    </>
                    :
                    (
                        <CommonRestricted />
                    )
            }


        </Box>
    )
}
